import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation, useCurrentLanguage } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import { useJsonLd } from '../hooks/useJsonLd'
import { buildServicePageGraph } from '../lib/schema'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'

const asArray = (value) => (Array.isArray(value) ? value : [])

const Emdr = () => {
  const t = useTranslation()
  const { isLoading, language } = useLanguage()
  const currentLanguage = useCurrentLanguage()
  const location = useLocation()

  usePageSeo({
    enabled: !isLoading,
    title: `${t('emdr.meta.title')} | VIVA Psychotherapy`,
    description: t('emdr.meta.description'),
    pathname: location.pathname
  })

  const faqItems = asArray(t('emdr.faq.items'))

  const emdrSchema = useMemo(
    () =>
      buildServicePageGraph({
        pathname: location.pathname,
        pageName: `${t('emdr.meta.title')} | VIVA Psychotherapy`,
        pageDescription: t('emdr.meta.description'),
        serviceName: t('emdr.hero.title'),
        serviceDescription: t('emdr.whatIs.body'),
        faqItems,
        breadcrumbMiddle: { name: t('common.nav.emdr'), url: location.pathname }
      }),
    [location.pathname, t, faqItems, isLoading]
  )

  useJsonLd('data-emdr-schema', emdrSchema, !isLoading)

  const contactPath = language === 'pt' ? '/pt/contact' : '/contact'
  const homePath = language === 'pt' ? '/pt' : '/'
  const teamMemberPath = (slug) => (language === 'pt' ? `/pt/team/${slug}` : `/team/${slug}`)
  const emdrFilesLang = language === 'pt' ? 'pt' : 'en'

  const differenceItems = asArray(t('emdr.difference.items'))
  const phaseItems = asArray(t('emdr.phases.items'))
  const whoList = asArray(t('emdr.whoBenefits.list'))
  const benefitItems = asArray(t('emdr.benefits.items'))
  const accessibilityHighlights = asArray(t('emdr.accessibility.highlights'))
  const downloadItems = asArray(t('emdr.downloads.items'))
  const specialistsMembers = asArray(t('emdr.specialists.members'))

  const phasesDiagramSrc =
    language === 'pt' ? '/img/emdr/diagram_8FasesPOR.png' : '/img/emdr/diagram_8Phases_ENG.png'
  const [phasesDiagramLightboxOpen, setPhasesDiagramLightboxOpen] = useState(false)

  useEffect(() => {
    if (!phasesDiagramLightboxOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [phasesDiagramLightboxOpen])

  useEffect(() => {
    if (!phasesDiagramLightboxOpen) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPhasesDiagramLightboxOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [phasesDiagramLightboxOpen])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading…</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite">
      <section className="bg-gradient-to-br from-primary-darkGreen to-primary-darkGreen/85 py-14 lg:py-20 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{t('emdr.hero.title')}</h1>
          <p className="text-lg md:text-xl text-white/90 leading-relaxed">{t('emdr.hero.subtitle')}</p>
        </div>
      </section>

      <section className="py-12 lg:py-16 border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
              {t('emdr.whatIs.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">{t('emdr.whatIs.body')}</p>
            <figure className="mt-6 rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white">
              <img
                src="/img/emdr/emdr-session-1.png"
                alt={t('emdr.images.session1Alt')}
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
              />
            </figure>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
              {t('emdr.howItWorks.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">{t('emdr.howItWorks.body')}</p>
            <figure className="mt-6 rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white">
              <img
                src="/img/emdr/emdr-session-2.png"
                alt={t('emdr.images.session2Alt')}
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
              />
            </figure>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-6">
            {t('emdr.difference.title')}
          </h2>
          <ul className="space-y-5 mb-14">
            {differenceItems.map((item, i) => (
              <li key={i} className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-primary-darkGreen">{item.heading}: </span>
                {item.text}
              </li>
            ))}
          </ul>

          <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
            {t('emdr.phases.title')}
          </h2>
          <figure className="relative mb-8 rounded-xl overflow-hidden border border-primary-darkGreen/15 bg-primary-offWhite shadow-sm">
            <img
              src={phasesDiagramSrc}
              alt={t('emdr.images.phasesDiagramAlt')}
              className="w-full h-auto"
              loading="lazy"
              decoding="async"
            />
            <button
              type="button"
              onClick={() => setPhasesDiagramLightboxOpen(true)}
              className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 inline-flex items-center justify-center rounded-lg bg-white/95 p-2 sm:p-2.5 text-primary-darkGreen shadow-md ring-1 ring-primary-darkGreen/15 transition hover:bg-white hover:ring-primary-darkGreen/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-darkGreen focus-visible:ring-offset-2"
              aria-label={t('emdr.images.expandPhasesDiagram')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-5 sm:size-6 shrink-0"
                aria-hidden
              >
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
          </figure>
          <ol className="list-decimal pl-5 space-y-4 text-gray-700 leading-relaxed marker:text-primary-darkGreen marker:font-semibold">
            {phaseItems.map((item, i) => (
              <li key={i}>
                <span className="font-semibold text-gray-900">{item.title}. </span>
                <span>{item.text}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
            {t('emdr.whoBenefits.title')}
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg mb-6">{t('emdr.whoBenefits.intro')}</p>
          <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-12">
            {whoList.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>

          <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
            {t('emdr.firstStep.title')}
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg mb-6">{t('emdr.firstStep.body')}</p>
          <figure className="mb-12 rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white">
            <img
              src="/img/emdr/emdr-session3.png"
              alt={t('emdr.images.session3Alt')}
              className="w-full h-auto object-cover"
              loading="lazy"
              decoding="async"
            />
          </figure>

          <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
            {t('emdr.whyViva.title')}
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg mb-12">{t('emdr.whyViva.body')}</p>

          <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
            {t('emdr.specialists.title')}
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg mb-8">{t('emdr.specialists.body')}</p>
          <div
            className="flex flex-wrap justify-center items-start gap-8 sm:gap-12 md:gap-16"
            role="list"
            aria-label={t('emdr.specialists.title')}
          >
            {specialistsMembers.map((member) => (
              <Link
                key={member.slug}
                to={teamMemberPath(member.slug)}
                className="group flex flex-col items-center shrink-0 w-[5.5rem] sm:w-28 md:w-32 lg:w-36 min-w-0 px-0.5 pt-2"
                role="listitem"
              >
                <div className="relative mx-auto h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 rounded-full overflow-hidden bg-neutral-200 ring-2 ring-primary-darkGreen/20 shadow-md shadow-black/10 transition-[box-shadow,ring-color] duration-300 ease-out group-hover:ring-primary-darkGreen/50 group-hover:shadow-md group-hover:shadow-black/15">
                  <img
                    src={member.avatar}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <p className="mt-2.5 sm:mt-3 text-center text-[0.7rem] sm:text-xs md:text-sm font-semibold text-gray-900 leading-snug max-w-[5.5rem] sm:max-w-[8rem] md:max-w-[9rem] line-clamp-3">
                  {member.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-white border-y border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
            {t('emdr.accessibility.title')}
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg mb-6">{t('emdr.accessibility.intro')}</p>
          <figure className="mb-8 max-w-md mx-auto rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white">
            <img
              src="/img/emdr/emdr-tappers.jpeg"
              alt={t('emdr.images.tappersAlt')}
              className="w-full h-auto object-cover"
              loading="lazy"
              decoding="async"
            />
          </figure>
          <ul className="space-y-4 mb-10">
            {accessibilityHighlights.map((h, i) => (
              <li
                key={i}
                className="rounded-lg border border-primary-darkGreen/15 bg-primary-darkGreen/[0.04] px-4 py-3 text-gray-800"
              >
                <span className="font-semibold text-primary-darkGreen">{h.heading}: </span>
                {h.text}
              </li>
            ))}
          </ul>
          <h3 className="text-xl font-bold text-primary-darkGreen mb-3">{t('emdr.accessibility.missionTitle')}</h3>
          <p className="text-gray-700 leading-relaxed text-lg mb-8">{t('emdr.accessibility.missionBody')}</p>
          <p className="text-gray-800 font-medium text-lg mb-6">{t('emdr.accessibility.barrierNote')}</p>
          <div className="rounded-xl bg-primary-darkGreen/10 border border-primary-darkGreen/20 px-6 py-6 text-center">
            <p className="font-bold text-primary-darkGreen text-lg mb-2">{t('emdr.finalCta.title')}</p>
            <p className="text-gray-700 mb-5">{t('emdr.finalCta.body')}</p>
            <Button asChild variant="brand" size="lg">
              <Link to={contactPath}>{t('emdr.cta.contact')}</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-start">
            {/* Benefits — logo in fixed left column; text gets full remaining width (no overlap) */}
            <div className="rounded-2xl border border-primary-darkGreen/15 bg-gradient-to-br from-white via-primary-offWhite to-primary-darkGreen/[0.06] p-6 sm:p-8 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-[10.5rem_minmax(0,1fr)] md:grid-cols-[11.5rem_minmax(0,1fr)] gap-6 sm:gap-8 items-start">
                <div className="flex justify-center sm:justify-start sm:pt-0.5">
                  <img
                    src="/img/logo/Logo-nobg-300x300.png"
                    alt=""
                    aria-hidden
                    className="pointer-events-none select-none w-36 sm:w-40 md:w-44 h-auto max-h-44 object-contain object-left opacity-45"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="min-w-0 text-center sm:text-left">
                  <h2 className="text-xl font-bold text-primary-darkGreen mb-4">{t('emdr.benefits.title')}</h2>
                  <ul className="space-y-4 text-left">
                    {benefitItems.map((b, i) => (
                      <li key={i} className="text-gray-700 leading-relaxed text-pretty">
                        <span className="font-semibold text-gray-900">{b.heading}: </span>
                        {b.text}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="min-w-0">
              <div className="rounded-2xl border border-primary-darkGreen/20 bg-white p-5 sm:p-6 shadow-md">
                <h3 className="text-lg sm:text-xl font-bold text-primary-darkGreen">{t('emdr.downloads.title')}</h3>
                <p className="text-sm text-gray-600 mt-1 mb-4">{t('emdr.downloads.subtitle')}</p>
                <nav aria-label={t('emdr.downloads.title')}>
                  <ul className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden mb-5">
                    {downloadItems.map((item, i) => (
                      <li key={i}>
                        <a
                          href={`/img/emdr/files/${emdrFilesLang}/${encodeURIComponent(item.file)}`}
                          download
                          className="group flex items-center gap-2.5 px-3.5 py-2.5 sm:py-3 text-primary-darkGreen font-medium text-sm sm:text-base hover:bg-primary-darkGreen/[0.06] active:bg-primary-darkGreen/10 transition-colors"
                        >
                          <svg
                            className="w-4 h-4 shrink-0 text-primary-darkGreen/70"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <span className="min-w-0 leading-snug group-hover:underline underline-offset-2 decoration-primary-darkGreen/50">
                            {item.label}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
                <Link
                  to={contactPath}
                  className="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-lg border-2 border-primary-darkGreen text-primary-darkGreen text-sm sm:text-base font-semibold hover:bg-primary-darkGreen hover:text-white transition-colors"
                >
                  {t('emdr.cta.contact')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">{t('emdr.faq.title')}</h2>
          </div>

          <Accordion
            type="multiple"
            className="rounded-lg border border-border bg-card px-4 shadow-sm sm:px-6"
          >
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`emdr-faq-${index}`}>
                <AccordionTrigger className="text-left text-lg font-semibold text-foreground hover:text-primary hover:no-underline md:text-xl">
                  {index + 1}. {item.question}
                </AccordionTrigger>
                <AccordionContent forceMount>
                  <p className="leading-relaxed text-foreground">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link
            to={homePath}
            className="inline-flex items-center text-primary-darkGreen font-semibold hover:underline"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('emdr.cta.backHome')}
          </Link>
        </div>
      </section>

      {phasesDiagramLightboxOpen ? (
        <div
          className="fixed inset-0 z-[100]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="emdr-phases-diagram-lightbox-title"
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-[2px]"
            onClick={() => setPhasesDiagramLightboxOpen(false)}
            aria-hidden
          />
          <span id="emdr-phases-diagram-lightbox-title" className="sr-only">
            {t('emdr.images.phasesDiagramAlt')}
          </span>
          <button
            type="button"
            onClick={() => setPhasesDiagramLightboxOpen(false)}
            className="absolute top-4 right-4 z-[102] inline-flex items-center justify-center rounded-full bg-white p-2.5 text-primary-darkGreen shadow-lg ring-1 ring-black/10 transition hover:bg-primary-offWhite focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-darkGreen"
            aria-label={t('emdr.images.closeExpandedDiagram')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-6"
              aria-hidden
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4 pt-16 sm:p-6 sm:pt-20">
            <div
              className="pointer-events-auto max-h-[min(92vh,56rem)] w-full max-w-[min(96vw,80rem)]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={phasesDiagramSrc}
                alt={t('emdr.images.phasesDiagramAlt')}
                className="mx-auto max-h-[min(92vh,56rem)] w-full object-contain object-center rounded-lg shadow-2xl ring-1 ring-white/20"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default Emdr
